import { useRef } from "react";
import { translate } from "../../../core";
import { SearchModal, SearchProps } from "../../../core/component/Search";
import { Combo } from "../../../core/component";
import { Contact } from "../../contact/type/contact";
import { QueryPageableRequest } from "../../../core/type/graphql";
import { QueryPageableResponse } from "../../../core/type/graphql";
import { GetVendorsForLookup } from "../../contact/graphql/contact.queries.graphql";
import { useLazyQuery } from "@apollo/client";
import { ContactFindRequest } from "../../contact/type/contact.query";
import { stringConcat } from "../../../core/until";
export interface PurchaseOrderStatus {
  value: string;
  display: string;
}

export type PurchaseSearchProps = Omit<SearchProps, "onComplete"> & {
  initial?: Record<string, string>;
  onComplete?: (request: any, labels?: Record<string, string>) => void;
};

export const PurchaseSearch = ({
  open,
  closeMe,
  onPageChange,
  onComplete,
  initial = {},
}: PurchaseSearchProps) => {
  const [findVendors] = useLazyQuery<
    QueryPageableResponse<Contact, "contactsFind">,
    QueryPageableRequest<ContactFindRequest>
  >(GetVendorsForLookup);

  const initialVendorId = initial.vendorId ? Number(initial.vendorId) : undefined;
  const vendorLabels = useRef<Record<number, string>>(
    initialVendorId != null && initial.vendorName
      ? { [initialVendorId]: initial.vendorName }
      : {}
  );

  return (
    <SearchModal<any>
      title={translate("search", "purchase_order")}
      open={open}
      closeMe={closeMe}
      onComplete={(request, page) => {
        onPageChange?.(page ?? 1);
        if (request) {
          const vendorId = Number(request.vendorId);
          const vendorName = vendorLabels.current[vendorId];

          onComplete?.(request, vendorName ? { vendorId: vendorName } : undefined);
        }
        closeMe();
      }}
      idLabel={translate("purchase_order_nu_abbr")}
      defaultId={initial.number}
      searchOptions={[
        {
          key: "vendor",
          label: translate("vendor"),
          defaultValue: initialVendorId,
          defaultActive: initialVendorId != null,
          render: ({ isActive }) => (
            <Combo<Contact, number>
              disabled={!isActive}
              size="middle"
              className="w-100"
              fetch={async ({ request, search, value }) => {
                if (value != null && !search) {
                  const known = vendorLabels.current[Number(value)];
                  if (known)
                    return [{ value: Number(value), display: known, item: {} as Contact }];
                }

                const { data } = await findVendors({
                  variables: {
                    request: {
                      take: request?.take ?? 100,
                      skip: request?.skip ?? 0,
                      searchVendor: search,
                    },
                  },
                });

                const list = data?.contactsFind.data ?? [];

                return list.map((e) => {
                  const display = stringConcat(e.contactId, " - ", e.company);
                  vendorLabels.current[e.id || 0] = display;

                  return {
                    value: e.id || 0,
                    display,
                    item: e || {},
                  };
                });
              }}
            />
          ),
          hideExactMatch: true,
          setValue: (params, request) => {
            request.vendorId = params.value;
          },
        },
        {
          key: "status",
          label: translate("status"),
          defaultValue: initial.status,
          defaultActive: !!initial.status,
          render: ({ isActive }) => (
            <Combo<PurchaseOrderStatus, PurchaseOrderStatus>
              disabled={!isActive}
              size="middle"
              className="w-100"
              fetch={async ({ request, search }) => {
                return [
                  { value: "Pending", display: "Pending" },
                  { value: "Confirmed", display: "Confirmed" },
                  { value: "PartiallyReceived", display: "Partially Received" },
                  { value: "Completed", display: "Received Complete" },
                ];
              }}
            />
          ),
          hideExactMatch: true,
          setValue: (params, request) => {
            request.status = params.value;
          },
        },
      ]}
      findPage={async (id) => {
        const number = Number(id);
        onComplete?.(Number.isInteger(number) ? { number } : {});
        return { page: 1 };
      }}
      findPageLoading={false}
    />
  );
};
